import { RpcClient } from "effect/rpc";
import { FrontDeskKioskOperationsRpcs } from "@ascleo/frontdesk-contracts/kiosk-operations/rpc";
import type {
  KioskCreatePatientRequest,
  KioskPatientCreationRejected,
} from "@ascleo/frontdesk-contracts/kiosk-operations/create-patient";
import type {
  KioskCreatePrescriptionRequest,
  KioskPrescriptionRequestRejected,
} from "@ascleo/frontdesk-contracts/kiosk-operations/prescription-request";
import { Effect } from "effect";
import { getDocCirrusRpcLayer } from "./bridge-rpc.transport";

/**
 * Patient-facing result of one policy-governed kiosk creation attempt.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const outcome: KioskPatientCreationOutcome = {
 *   kind: "policy-restricted",
 * };
 * ```
 */
export type KioskPatientCreationOutcome =
  | { readonly kind: "created"; readonly patientId: string }
  | { readonly kind: "policy-restricted" }
  | { readonly kind: "failed" };

/**
 * Patient-facing result of one policy-aware prescription submission.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const outcome: KioskPrescriptionSubmissionOutcome = {
 *   kind: "policy-restricted",
 * };
 * ```
 */
export type KioskPrescriptionSubmissionOutcome =
  | { readonly kind: "submitted"; readonly taskId: string }
  | { readonly kind: "policy-restricted" }
  | { readonly kind: "failed" };

/**
 * App-facing policy-governed kiosk mutation client.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * declare const request: KioskCreatePatientRequest;
 *
 * const outcome = await kioskOperationsClient.createPatient(request);
 * ```
 */
export interface KioskOperationsClient {
  readonly createPatient: (
    request: KioskCreatePatientRequest,
  ) => Promise<KioskPatientCreationOutcome>;
  readonly createPrescriptionRequest: (
    request: KioskCreatePrescriptionRequest,
  ) => Promise<KioskPrescriptionSubmissionOutcome>;
}

/**
 * Maps one decoded server rejection into the narrow patient-facing outcome.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const outcome = classifyKioskPatientCreationRejection({
 *   _tag: "KioskPatientCreationRejected",
 *   reason: "policy-disabled",
 * });
 * ```
 */
export function classifyKioskPatientCreationRejection(
  rejection: KioskPatientCreationRejected,
): KioskPatientCreationOutcome {
  return rejection.reason === "creation-failed"
    ? { kind: "failed" }
    : { kind: "policy-restricted" };
}

/**
 * Maps one decoded prescription rejection into a patient-facing outcome.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const outcome = classifyKioskPrescriptionRequestRejection({
 *   _tag: "KioskPrescriptionRequestRejected",
 *   reason: "policy-disabled",
 * });
 * ```
 */
export function classifyKioskPrescriptionRequestRejection(
  rejection: KioskPrescriptionRequestRejected,
): KioskPrescriptionSubmissionOutcome {
  return rejection.reason === "policy-disabled" ||
    rejection.reason === "policy-unavailable"
    ? { kind: "policy-restricted" }
    : { kind: "failed" };
}

async function createPatient(
  request: KioskCreatePatientRequest,
): Promise<KioskPatientCreationOutcome> {
  try {
    return await Effect.runPromise(
      Effect.scoped(
        Effect.gen(function* () {
          const client = yield* RpcClient.make(FrontDeskKioskOperationsRpcs);
          const response = yield* client.FrontDeskCreatePatient(request);
          return {
            kind: "created" as const,
            patientId: response.patientId,
          };
        }),
      ).pipe(
        Effect.catchTag(
          "KioskPatientCreationRejected",
          (rejection) =>
            Effect.succeed(
              classifyKioskPatientCreationRejection(rejection),
            ),
        ),
        Effect.catch(() =>
          Effect.succeed<KioskPatientCreationOutcome>({ kind: "failed" }),
        ),
        Effect.provide(getDocCirrusRpcLayer()),
      ),
    );
  } catch {
    return { kind: "failed" };
  }
}

async function createPrescriptionRequest(
  request: KioskCreatePrescriptionRequest,
): Promise<KioskPrescriptionSubmissionOutcome> {
  try {
    return await Effect.runPromise(
      Effect.scoped(
        Effect.gen(function* () {
          const client = yield* RpcClient.make(FrontDeskKioskOperationsRpcs);
          const response =
            yield* client.FrontDeskCreatePrescriptionRequest(request);
          return {
            kind: "submitted" as const,
            taskId: response.taskId,
          };
        }),
      ).pipe(
        Effect.catchTag(
          "KioskPrescriptionRequestRejected",
          (rejection) =>
            Effect.succeed(
              classifyKioskPrescriptionRequestRejection(rejection),
            ),
        ),
        Effect.catch(() =>
          Effect.succeed<KioskPrescriptionSubmissionOutcome>({
            kind: "failed",
          }),
        ),
        Effect.provide(getDocCirrusRpcLayer()),
      ),
    );
  } catch {
    return { kind: "failed" };
  }
}

/**
 * Shared client for policy-governed FrontDesk+ kiosk mutations.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const create = kioskOperationsClient.createPatient;
 * ```
 */
export const kioskOperationsClient: KioskOperationsClient = {
  createPatient,
  createPrescriptionRequest,
};
